import React from "react";
import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { Environment } from "@react-three/drei";
import { Suspense, useRef, useImperativeHandle, forwardRef, useEffect, useMemo } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls as OrbitControlsImpl } from "three/examples/jsm/controls/OrbitControls";
import { MeshoptDecoder } from "meshoptimizer";
import { CameraControlsButtons } from "./CameraControls";

function supportsWebGL() {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  const canvas = document.createElement("canvas");
  return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl") || canvas.getContext("experimental-webgl"));
}

class WebGLFallbackBoundary extends React.Component<
  { fallback: React.ReactNode; children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

// Register meshopt decoder on the GLTFLoader class so all instances support it
(GLTFLoader.prototype as any)._meshoptRegistered = false;
const _origParse = GLTFLoader.prototype.parse;
GLTFLoader.prototype.parse = function (data: any, path: string, onLoad: any, onError: any) {
  if (!(this as any)._meshoptRegistered) {
    (this as any).setMeshoptDecoder(MeshoptDecoder);
    (this as any)._meshoptRegistered = true;
  }
  return _origParse.call(this, data, path, onLoad, onError);
};

export const FINISH_PRESETS: Record<string, { color: string; metalness: number; roughness: number }> = {
  // Chrome finishes
  "chrome": { color: "#c8c8c8", metalness: 1.0, roughness: 0.08 },
  "hi-chrome": { color: "#e8e8e8", metalness: 1.0, roughness: 0.02 },
  // Plating finishes
  "gold-plated": { color: "#d4a843", metalness: 1.0, roughness: 0.03 },
  // Black finishes
  "hi-gloss-black": { color: "#0a0a0a", metalness: 0.6, roughness: 0.1 },
  "matte-black": { color: "#1a1a1a", metalness: 0.3, roughness: 0.7 },
  // Silver / brushed
  "brushed-chrome": { color: "#a0a0a0", metalness: 0.75, roughness: 0.25 },
  "silver-spray": { color: "#9a9a9a", metalness: 0.5, roughness: 0.35 },
  // Powder coat colors (flat, no mirror reflection)
  "powder-blue": { color: "#2a4a7a", metalness: 0.15, roughness: 0.65 },
  "powder-copper": { color: "#b87333", metalness: 0.2, roughness: 0.55 },
  "powder-red": { color: "#8a2a2a", metalness: 0.15, roughness: 0.65 },
  "powder-green": { color: "#2a5a3a", metalness: 0.15, roughness: 0.65 },
  "powder-white": { color: "#e8e4dc", metalness: 0.1, roughness: 0.7 },
  "powder-yellow": { color: "#c9a832", metalness: 0.15, roughness: 0.6 },
};

function Model({ url, rotation, finish, dualFinish }: {
  url: string;
  rotation?: [number, number, number];
  finish?: string;
  dualFinish?: { top: string; barrel: string; splitY?: number };
}) {
  const gltf = useLoader(GLTFLoader, url);
  const clonedScene = useMemo(() => {
    const clone = gltf.scene.clone(true);
    const box = new THREE.Box3().setFromObject(clone);
    const center = box.getCenter(new THREE.Vector3());
    clone.position.sub(center);
    clone.updateMatrixWorld(true);
    return clone;
  }, [gltf]);

  useEffect(() => {
    clonedScene.updateMatrixWorld(true);
    if (dualFinish) {
      const topPreset = FINISH_PRESETS[dualFinish.top];
      const barrelPreset = FINISH_PRESETS[dualFinish.barrel];
      if (!topPreset || !barrelPreset) return;

      clonedScene.traverse((child) => {
        if (child instanceof THREE.Mesh && child.geometry) {
          const geo = child.geometry;
          const pos = geo.attributes.position;
          const nor = geo.attributes.normal;
          if (!pos) return;

          const bbox = new THREE.Box3().setFromObject(clonedScene);
          const upperY = bbox.min.y + (bbox.max.y - bbox.min.y) * 0.5;

          const colors = new Float32Array(pos.count * 3);
          const topColor = new THREE.Color(topPreset.color);
          const barrelColor = new THREE.Color(barrelPreset.color);

          const worldMatrix = child.matrixWorld;
          const normalMatrix = new THREE.Matrix3().getNormalMatrix(worldMatrix);
          const tempVec = new THREE.Vector3();
          const tempNor = new THREE.Vector3();

          for (let i = 0; i < pos.count; i++) {
            tempVec.set(pos.getX(i), pos.getY(i), pos.getZ(i));
            tempVec.applyMatrix4(worldMatrix);

            let isTop = false;
            if (nor) {
              tempNor.set(nor.getX(i), nor.getY(i), nor.getZ(i));
              tempNor.applyMatrix3(normalMatrix).normalize();
              isTop = tempNor.y > 0.3 && tempVec.y > upperY;
            } else {
              isTop = tempVec.y > upperY;
            }

            const c = isTop ? topColor : barrelColor;
            colors[i * 3] = c.r;
            colors[i * 3 + 1] = c.g;
            colors[i * 3 + 2] = c.b;
          }

          geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));

          const mat = child.material as THREE.MeshStandardMaterial;
          if (mat.isMeshStandardMaterial) {
            mat.vertexColors = true;
            mat.map = null;
            mat.metalnessMap = null;
            mat.roughnessMap = null;
            mat.normalMap = null;
            mat.metalness = (topPreset.metalness + barrelPreset.metalness) / 2;
            mat.roughness = (topPreset.roughness + barrelPreset.roughness) / 2;
            mat.needsUpdate = true;
          }
        }
      });
    } else if (finish) {
      const preset = FINISH_PRESETS[finish];
      if (!preset) return;
      const bbox = new THREE.Box3().setFromObject(clonedScene);
      const panCenterY = bbox.min.y + (bbox.max.y - bbox.min.y) * 0.55;

      clonedScene.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material) {
          const mat = child.material as THREE.MeshStandardMaterial;
          if (!mat.isMeshStandardMaterial) return;

          const name = child.name.toLowerCase();
          const isStand = /stand|leg|frame|support|base|strut|brace|rod|wheel|caster/i.test(name);
          let isBelowPan = false;
          if (!isStand) {
            const childBox = new THREE.Box3().setFromObject(child);
            const childMidY = (childBox.min.y + childBox.max.y) / 2;
            isBelowPan = childMidY < panCenterY - (bbox.max.y - bbox.min.y) * 0.15;
          }

          if (isStand || isBelowPan) {
            mat.vertexColors = false;
            mat.map = null;
            mat.metalnessMap = null;
            mat.roughnessMap = null;
            mat.normalMap = null;
            mat.color.set("#1a1a1a");
            mat.metalness = 0.7;
            mat.roughness = 0.3;
            mat.needsUpdate = true;
          } else {
            mat.vertexColors = false;
            mat.map = null;
            mat.metalnessMap = null;
            mat.roughnessMap = null;
            mat.normalMap = null;
            mat.color.set(preset.color);
            mat.metalness = preset.metalness;
            mat.roughness = preset.roughness;
            mat.needsUpdate = true;
          }
        }
      });
    }
  }, [finish, dualFinish, clonedScene]);

  return (
    <primitive object={clonedScene} rotation={rotation || [0, 0, 0]} />
  );
}

export interface SinglePanSceneHandle {
  resetView: () => void;
}

export interface LightingOverrides {
  ambient?: number;
  spotFront?: number;
  spotBack?: number;
  pointFill?: number;
  envIntensity?: number;
}

export interface SinglePanSceneProps {
  modelUrl: string;
  rotation?: [number, number, number];
  cameraPosition?: [number, number, number];
  darkMode?: boolean;
  autoRotate?: boolean;
  finish?: string;
  dualFinish?: { top: string; barrel: string };
  envPreset?: string;
  lighting?: LightingOverrides;
  onDebug?: (message: string) => void;
}

const SceneOrbitControls = forwardRef(function SceneOrbitControls(
  {
    autoRotate,
    minPolarAngle,
    maxPolarAngle,
    target,
    minDistance,
    maxDistance,
  }: {
    autoRotate?: boolean;
    minPolarAngle?: number;
    maxPolarAngle?: number;
    target?: [number, number, number];
    minDistance?: number;
    maxDistance?: number;
  },
  ref,
) {
  const { camera, gl } = useThree();
  const controls = useMemo(() => new OrbitControlsImpl(camera, gl.domElement), [camera, gl.domElement]);

  useImperativeHandle(ref, () => controls, [controls]);

  useEffect(() => {
    controls.enablePan = false;
    controls.autoRotate = Boolean(autoRotate);
    controls.autoRotateSpeed = 1.5;
    controls.minPolarAngle = minPolarAngle ?? Math.PI / 4;
    controls.maxPolarAngle = maxPolarAngle ?? Math.PI / 1.6;
    controls.minDistance = minDistance ?? 1.5;
    controls.maxDistance = maxDistance ?? 6;
    if (target) {
      controls.target.set(target[0], target[1], target[2]);
    }
    controls.update();
    return () => controls.dispose();
  }, [controls, autoRotate, minPolarAngle, maxPolarAngle, target, minDistance, maxDistance]);

  useFrame(() => controls.update());
  return null;
});

const SinglePanScene = forwardRef<SinglePanSceneHandle, SinglePanSceneProps>(
  function SinglePanScene({ modelUrl, rotation, cameraPosition, darkMode = false, autoRotate = false, finish, dualFinish, envPreset = "studio", lighting, onDebug }, ref) {
    const isDark = envPreset === "night" || darkMode;
    const bg = isDark ? "#000000" : "#111111";
    const controlsRef = useRef<any>(null);
    const webglSupported = useMemo(() => supportsWebGL(), []);
    const [webglLost, setWebglLost] = React.useState(false);

    useImperativeHandle(ref, () => ({
      resetView: () => {
        if (controlsRef.current) {
          controlsRef.current.reset();
        }
      },
    }));

    return (
      <div className="relative w-full h-screen" style={{ background: bg }}>
        {!webglSupported ? (
          <div className="flex min-h-screen items-center justify-center px-6 text-center" style={{ background: bg }}>
            <div className="max-w-md">
              <p className={`text-sm tracking-widest uppercase ${darkMode ? "text-white/40" : "text-white/50"}`}>
                Visualizer unavailable
              </p>
              <p className={`mt-3 text-sm leading-relaxed ${darkMode ? "text-white/55" : "text-white/60"}`}>
                This browser or device cannot create a WebGL context. Try a different browser, enable hardware acceleration, or use the text-based pages instead.
              </p>
            </div>
          </div>
        ) : (
        <Suspense fallback={<div className="flex min-h-screen items-center justify-center" style={{ background: bg }}><p className={`text-sm tracking-widest uppercase animate-pulse ${darkMode ? "text-white/40" : "text-black/40"}`}>Initializing…</p></div>}>
          <WebGLFallbackBoundary fallback={<div className="flex min-h-screen items-center justify-center px-6 text-center" style={{ background: bg }}><div className="max-w-md"><p className={`text-sm tracking-widest uppercase ${darkMode ? "text-white/40" : "text-white/50"}`}>Visualizer unavailable</p><p className={`mt-3 text-sm leading-relaxed ${darkMode ? "text-white/55" : "text-white/60"}`}>The 3D scene could not start in this browser. Reload after enabling hardware acceleration or open the page on a device with WebGL support.</p></div></div>}>
          <Canvas
            camera={{ position: cameraPosition || [0, 0.3, 2.2], fov: 35 }}
            dpr={[1, 1.5]}
            gl={{ antialias: true, alpha: false, powerPreference: "low-power" }}
            style={{ background: bg }}
            onCreated={({ gl }) => {
              const onLost = (event: Event) => {
                event.preventDefault();
                setWebglLost(true);
              };
              gl.domElement.addEventListener("webglcontextlost", onLost, false);
            }}
          >
            <color attach="background" args={[bg]} />

            <ambientLight intensity={lighting?.ambient ?? (isDark ? 0.5 : 0.5)} />
            <spotLight
              position={[5, 8, 8]}
              angle={0.3}
              penumbra={1}
              intensity={lighting?.spotFront ?? (isDark ? 2.0 : 1.5)}
              castShadow
            />
            <spotLight
              position={[-5, 8, -8]}
              angle={0.3}
              penumbra={1}
              intensity={lighting?.spotBack ?? (isDark ? 1.5 : 1.0)}
            />
            <pointLight position={[0, -2, 5]} intensity={lighting?.pointFill ?? (isDark ? 0.8 : 0.5)} />
            <Environment preset={isDark ? "night" : "city"} background={false} environmentIntensity={lighting?.envIntensity ?? (isDark ? 0.6 : 1.0)} />

            <Suspense
              fallback={
                <mesh>
                  <boxGeometry args={[0.4, 0.4, 0.4]} />
                  <meshStandardMaterial color="#888888" />
                </mesh>
              }
            >
              <Model url={modelUrl} rotation={rotation} finish={finish} dualFinish={dualFinish} />
            </Suspense>
            <SceneOrbitControls
              ref={controlsRef}
              autoRotate={autoRotate}
              minPolarAngle={Math.PI / 4}
              maxPolarAngle={Math.PI / 1.6}
              target={[0, 0.15, 0]}
              minDistance={1.5}
              maxDistance={6}
            />
          </Canvas>
          </WebGLFallbackBoundary>
        </Suspense>
        )}
        {/* Camera controls */}
        <div className="absolute bottom-6 right-6 z-10">
          <CameraControlsButtons controlsRef={controlsRef} />
        </div>
        {webglLost && (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center" style={{ background: bg }}>
            <div className="max-w-md">
              <p className={`text-sm tracking-widest uppercase ${darkMode ? "text-white/40" : "text-white/50"}`}>
                Visualizer unavailable
              </p>
              <p className={`mt-3 text-sm leading-relaxed ${darkMode ? "text-white/55" : "text-white/60"}`}>
                The WebGL context was lost while loading the scene. Reload after enabling hardware acceleration.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default SinglePanScene;
